/**
 * Item display helpers — UI shows names only, never raw item IDs.
 */
import { getItems } from '../api/inventoryMaster';

export const EMPTY_ITEM_LABEL = '—';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Normalize API list responses to a plain array. */
export function normalizeItemList(data) {
  return Array.isArray(data) ? data : (data?.items || data?.data || []);
}

export function getItemId(item) {
  return item?.item_id ?? item?.id ?? '';
}

export function isUuidLike(value) {
  return typeof value === 'string' && UUID_RE.test(value);
}

/**
 * Resolve the UOM id an item defaults to for a transaction role.
 * purchase -> default_purchase_uom_id, issue -> default_issue_uom_id, both
 * fall back to base_uom_id when unset (item master only requires base).
 */
export function defaultUom(item, role) {
  if (!item) return '';
  if (role === 'purchase') return item.default_purchase_uom_id || item.base_uom_id || '';
  if (role === 'issue') return item.default_issue_uom_id || item.base_uom_id || '';
  return item.base_uom_id || '';
}

/** Returns a display-safe name — rejects values that are IDs/UUIDs. */
export function meaningfulItemName(name, itemId) {
  if (!name?.trim()) return null;
  const trimmed = name.trim();
  if (trimmed === itemId || isUuidLike(trimmed)) return null;
  return trimmed;
}

/** Item name only (no code, no ID). */
export function itemNameOnly(item) {
  if (!item) return null;
  const id = getItemId(item);
  return (
    meaningfulItemName(item.item_name, id)
    || meaningfulItemName(item.item_short_name, id)
    || null
  );
}

/** Missing `is_active` is treated as active — same convention as vendors. */
export function isItemActive(item) {
  return item?.is_active !== false;
}

export function filterActiveItems(list) {
  return (Array.isArray(list) ? list : []).filter(isItemActive);
}

export function inactiveItemMessage(item) {
  const name = itemNameOnly(item) || item?.item_code || 'This item';
  return `${name} is inactive and cannot be used.`;
}

/** Map backend "Item '<uuid>' is inactive…" into a name-based message. */
export function formatInactiveItemApiError(err, fallbackItem) {
  const raw = err?.message || '';
  if (/inactiv/i.test(raw)) return inactiveItemMessage(fallbackItem);
  return raw || 'Failed to save. Please try again.';
}

function resolveFromArray(row, items) {
  if (!row) return EMPTY_ITEM_LABEL;
  const itemId = row.item_id || getItemId(row);
  const fromRow = meaningfulItemName(row.item_name, itemId);
  if (fromRow) return fromRow;
  if (!itemId || !items?.length) return EMPTY_ITEM_LABEL;
  const master = items.find(i => getItemId(i) === itemId);
  return itemNameOnly(master) || EMPTY_ITEM_LABEL;
}

/**
 * Resolve a user-visible item name from a line row.
 * @param {object} row — API line with item_id / item_name
 * @param {object[]|ReturnType<createItemCatalog>} catalog — array or indexed catalog
 */
export function resolveItemDisplayName(row, catalog = []) {
  if (catalog && typeof catalog.resolve === 'function') {
    return catalog.resolve(row);
  }
  return resolveFromArray(row, catalog);
}

/** For select / autocomplete option labels — name only. */
export function itemOptionLabel(item) {
  return itemNameOnly(item) || EMPTY_ITEM_LABEL;
}

export function itemOptionLabelWithBatch(item) {
  const name = itemOptionLabel(item);
  const batch = item?.batch_number;
  return batch ? `${name} [${batch}]` : name;
}

/**
 * Build an indexed catalog for fast repeated lookups (grids, PDFs, etc.).
 */
export function createItemCatalog(items = []) {
  const list = normalizeItemList(items);
  const byId = new Map();

  for (const item of list) {
    const id = getItemId(item);
    if (id) byId.set(id, item);
  }

  function resolveById(itemId) {
    if (!itemId) return EMPTY_ITEM_LABEL;
    return itemNameOnly(byId.get(itemId)) || EMPTY_ITEM_LABEL;
  }

  function resolve(row) {
    if (!row) return EMPTY_ITEM_LABEL;
    const itemId = row.item_id || getItemId(row);
    const fromRow = meaningfulItemName(row.item_name, itemId);
    if (fromRow) return fromRow;
    return resolveById(itemId);
  }

  // ── Extended lookup helpers (additive) ─────────────────────
  // Each returns a safe default so callers never have to null-check.

  function getBarcode(id) {
    return byId.get(id)?.primary_barcode || null;
  }

  function getBaseUomCode(id) {
    return byId.get(id)?.base_uom?.uom_code || '';
  }

  function getPurchaseUomCode(id) {
    return byId.get(id)?.purchase_uom_code || '';
  }

  function getIssueUomCode(id) {
    return byId.get(id)?.issue_uom_code || '';
  }

  function getPurchaseUomId(id) {
    return defaultUom(byId.get(id), 'purchase');
  }

  function getIssueUomId(id) {
    return defaultUom(byId.get(id), 'issue');
  }

  function isControlled(id) {
    const row = byId.get(id);
    return Boolean(row?.is_controlled_drug || row?.is_narcotic);
  }

  function isBatchTracked(id) {
    return Boolean(byId.get(id)?.is_batch_tracked);
  }

  function isExpiryTracked(id) {
    return Boolean(byId.get(id)?.is_expiry_tracked);
  }

  function getItemCode(id) {
    return byId.get(id)?.item_code || '';
  }

  function getItemClass(id) {
    const ic = byId.get(id)?.item_class;
    return ic?.class_name ?? ic?.item_class_name ?? ic?.name ?? '';
  }

  function getMaterialGroup(id) {
    const mg = byId.get(id)?.material_group;
    return mg?.group_name ?? mg?.material_group_name ?? mg?.name ?? '';
  }

  return {
    items: list,
    byId,
    resolve,
    resolveById,
    optionLabel: itemOptionLabel,
    optionLabelWithBatch: itemOptionLabelWithBatch,
    getBarcode,
    getBaseUomCode,
    getPurchaseUomCode,
    getIssueUomCode,
    getPurchaseUomId,
    getIssueUomId,
    isControlled,
    isBatchTracked,
    isExpiryTracked,
    getItemCode,
    getItemClass,
    getMaterialGroup,
  };
}

/** Copy batch/expiry tracking flags from the item catalog onto a GRN line. */
export function enrichLineWithItemTracking(line, catalog) {
  if (!line?.item_id || !catalog?.byId) return line;
  const master = catalog.byId.get(line.item_id);
  if (!master) return line;
  return {
    ...line,
    is_batch_tracked: Boolean(master.is_batch_tracked),
    is_expiry_tracked: Boolean(master.is_expiry_tracked),
    item_code: master.item_code || line.item_code || '',
  };
}

let cachedCatalog = null;
let loadPromise = null;

export function invalidateItemCatalogCache() {
  cachedCatalog = null;
  loadPromise = null;
}

/** Shared cache — one GET /inventory/items/ for the whole inventory module. */
export function fetchItemCatalog({ force = false } = {}) {
  if (!force && cachedCatalog) return Promise.resolve(cachedCatalog);
  if (!force && loadPromise) return loadPromise;

  loadPromise = getItems()
    .then(normalizeItemList)
    .then(items => {
      cachedCatalog = createItemCatalog(items);
      return cachedCatalog;
    })
    .catch(err => {
      console.error('[itemDisplay] catalog fetch failed:', err);
      // Reset so the next call retries instead of returning a stale null promise
      loadPromise = null;
      throw err;
    });

  return loadPromise;
}